import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { enqueueSnackbar } from "notistack";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getAssetCategories } from "../../services/ValitanApiService";
import { exportFinalReport } from "../../services/PricingService";

const DEFAULT_CATEGORY = "ALL";

const parseListPayload = (response) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getCategoryCode = (category) =>
  String(category?.code ?? category?.category_code ?? category?.categoryCode ?? "").trim().toUpperCase();

const getCategoryName = (category) =>
  String(category?.name ?? category?.category_name ?? category?.categoryName ?? getCategoryCode(category)).trim();

const safeFilenamePart = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);

const downloadBlobResponse = (response, fallbackName) => {
  const blob = response?.data;
  if (!blob || blob.size === 0) {
    throw new Error("The generated export file was empty.");
  }

  const contentDisposition = response?.headers?.["content-disposition"] || "";
  const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  const filename = decodeURIComponent(filenameMatch?.[1] || fallbackName);

  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
};

const FinalAssetReports = () => {
  document.title = "Final Reports | Valitan";

  const [categories, setCategories] = useState([]);
  const [selectedCategoryCode, setSelectedCategoryCode] = useState(DEFAULT_CATEGORY);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await getAssetCategories(0, 1000);
        const list = parseListPayload(response)
          .map((category) => ({
            value: getCategoryCode(category),
            label: `${getCategoryCode(category)} - ${getCategoryName(category)}`,
          }))
          .filter((category) => category.value);

        if (isMounted) {
          list.unshift({ value: "ALL", label: "ALL - All Asset Categories" });
          setCategories(list);
          if (!list.some((category) => category.value === DEFAULT_CATEGORY) && list.length > 0) {
            setSelectedCategoryCode(list[0].value);
          }
        }
      } catch (error) {
        if (isMounted) {
          enqueueSnackbar("Failed to load asset categories.", { variant: "error" });
        }
      } finally {
        if (isMounted) setLoadingCategories(false);
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedCategory = useMemo(
    () => categories.find((category) => category.value === selectedCategoryCode) || null,
    [categories, selectedCategoryCode]
  );

  const handleGenerate = async (event) => {
    event.preventDefault();

    const categoryCode = String(selectedCategoryCode || "").trim().toUpperCase();

    if (!categoryCode) {
      enqueueSnackbar("Select an asset category.", { variant: "warning" });
      return;
    }

    setGenerating(true);
    try {
      const categoryPart = safeFilenamePart(selectedCategory?.label || categoryCode) || categoryCode.toLowerCase();
      const response = await exportFinalReport({
        categoryCode: categoryCode,
      });
      downloadBlobResponse(
        response,
        `Final_Report_${categoryPart}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      enqueueSnackbar("Final Report generated successfully.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to generate Final Report. Please try again.", { variant: "error" });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Asset Valuation" breadcrumbItem="Final Reports" />

        <Row>
          <Col lg={8}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                  <div>
                    <h4 className="card-title mb-1">Generate Final Report</h4>
                    <p className="text-muted mb-0">
                      Download a comprehensive Excel report containing adjusted valuation, depreciation, and remaining economic life metrics.
                    </p>
                  </div>
                  <span className="badge bg-primary-subtle text-primary">Excel .xlsx</span>
                </div>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleGenerate}>
                  <Row className="g-3 align-items-end">
                    <Col md={8}>
                      <Label className="form-label">Asset Category</Label>
                      <Select
                        isLoading={loadingCategories}
                        options={categories}
                        value={selectedCategory}
                        onChange={(option) => setSelectedCategoryCode(option?.value || "")}
                        placeholder="Select category"
                        isClearable={false}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </Col>
                    <Col md={4} className="d-grid">
                      <Button color="primary" type="submit" disabled={generating || loadingCategories} size="md">
                        {generating ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <i className="ri-file-excel-2-line me-2" />
                            Export Report
                          </>
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Report Details</h5>
              </CardHeader>
              <CardBody>
                <ul className="ps-3 mb-0 text-muted">
                  <li>Computes adjusted cost for base assets.</li>
                  <li>Calculates accumulated depreciation correctly.</li>
                  <li>Extracts location and condition metrics.</li>
                  <li>Exports clean format for external audits.</li>
                </ul>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default FinalAssetReports;
